// History-API router: every route also exists as a static HTML file, so deep
// links and reloads work without server rewrites.
import { type Route, parsePath } from '../registry';

type Render = (route: Route) => void;
let render: Render | null = null;
let guard: (() => boolean) | null = null;

/** Lets the current page veto navigation (e.g. while processing). */
export function setNavigationGuard(fn: (() => boolean) | null): void {
  guard = fn;
}

export function navigate(path: string, opts: { replace?: boolean } = {}): void {
  if (guard && !guard()) return;
  const url = new URL(path, location.href);
  if (url.pathname === location.pathname && !url.hash && !opts.replace) {
    window.scrollTo({ top: 0 });
    return;
  }
  history[opts.replace ? 'replaceState' : 'pushState'](null, '', url.pathname + url.search + url.hash);
  render?.(parsePath(url.pathname));
  if (url.hash) document.getElementById(decodeURIComponent(url.hash.slice(1)))?.scrollIntoView();
  else window.scrollTo({ top: 0 });
}

function onClick(e: MouseEvent): void {
  if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
  const a = (e.target as Element | null)?.closest?.('a');
  if (!a || a.target || a.hasAttribute('download') || a.getAttribute('rel')?.includes('external')) return;
  const url = new URL(a.href, location.href);
  if (url.origin !== location.origin) return;
  if (url.pathname === location.pathname && url.hash) return; // in-page anchor
  if (parsePath(url.pathname).kind === 'notFound' && !url.pathname.endsWith('/')) return; // static files
  e.preventDefault();
  navigate(url.pathname + url.hash);
}

export function startRouter(fn: Render): void {
  render = fn;
  window.addEventListener('popstate', () => {
    render?.(parsePath(location.pathname));
  });
  document.addEventListener('click', onClick);
  render(parsePath(location.pathname));
}
